import React, { useCallback, useId, useMemo, useState } from 'react'
import SelectionBox from './SelectionBox.component'
import type { SelectionBoxGroupProps } from './SelectionBox.types'
import { SelectionBoxGroupContext } from './SelectionBoxContext'
import './SelectionBox.css'

const SelectionBoxGroup: React.FC<SelectionBoxGroupProps> = ({
  name,
  value,
  defaultValue = '',
  label,
  options,
  disabled = false,
  darkMode = false,
  className = '',
  onChange,
  children,
}) => {
  const generatedName = useId()
  const [internalValue, setInternalValue] = useState(defaultValue)
  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internalValue

  const handleValueChange = useCallback(
    (nextValue: string): void => {
      if (!isControlled) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [isControlled, onChange],
  )

  const contextValue = useMemo(
    () => ({
      name: name ?? generatedName,
      selectedValue: currentValue,
      disabled,
      darkMode,
      onValueChange: handleValueChange,
    }),
    [currentValue, darkMode, disabled, generatedName, handleValueChange, name],
  )

  const classNames = [
    'ds-selection-box-group',
    darkMode ? 'ds-selection-box-group--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <SelectionBoxGroupContext.Provider value={contextValue}>
      <div
        className={classNames}
        role="radiogroup"
        aria-label={typeof label === 'string' ? label : undefined}
      >
        {label ? (
          <span className="ds-selection-box-group__legend">{label}</span>
        ) : null}
        <div className="ds-selection-box-group__options">
          {options
            ? options.map((option) => (
                <SelectionBox
                  key={option.value}
                  value={option.value}
                  title={option.title}
                  description={option.description}
                  disabled={option.disabled}
                  darkMode={darkMode}
                />
              ))
            : children}
        </div>
      </div>
    </SelectionBoxGroupContext.Provider>
  )
}

export default SelectionBoxGroup
